import { DialogSteps, toaster } from '@welbe/design-system';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { ProfileSelection } from './components/ProfileSelection';
import { ConfirmProfile } from './components/ConfirmProfile';
import { ConfirmFusion } from './components/ConfirmFusion';
import {
	getPendingAppointments,
	postMerge,
} from './MergeEmployeesDialog.services';
import type { StepProps } from '@welbe/design-system';
import type {
	EmployeeProps,
	MergedStepComponentProps,
} from './MergeEmployeesDialog.types';

const steps: StepProps[] = [
	{
		id: 0,
		title: 'Selecciona el perfil principal',
		component: ProfileSelection,
	},
	{
		id: 1,
		title: 'Confirmar perfil principal seleccionado',
		component: ConfirmProfile,
	},
	{
		id: 2,
		title: 'Confirmar fusión de expedientes',
		component: ConfirmFusion,
	},
];

type MergeEmployeesDialogProps = {
	open: boolean;
	employees: EmployeeProps[];
	onClose: (refresh?: boolean) => void;
};

export const MergeEmployeesDialog = ({
	open,
	employees,
	onClose,
}: MergeEmployeesDialogProps) => {
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const [step, setStep] = useState<number>(0);
	const [primaryEmployeeId, setPrimaryEmployeeId] = useState<string>('');
	const secondaryEmployeeId =
		employees.find((employee) => employee.id !== primaryEmployeeId)?.id || '';

	const handleSubmit = async (reason: string) => {
		try {
			setLoadingSubmit(true);
			await getPendingAppointments({ employeeId: primaryEmployeeId });
			await postMerge({
				reason,
				employeeOriginId: secondaryEmployeeId,
				employeeDestinationId: primaryEmployeeId,
			});
			toaster.create({
				description: '¡Los expedientes se fusionaron correctamente!',
				type: 'success',
			});
			onClose(true);
		} catch (error) {
			const parsed = JSON.parse(JSON.stringify(error) || '{}');
			toaster.create({
				title: 'No es posible fusionar los expedientes',
				description:
					parsed.status === 400
						? 'Alguno de los perfiles de colaborador que intentas fusionar tiene consultas abiertas o agendadas. Asegúrate de finalizar y/o cancelar las consultas antes de unificar los expedientes'
						: 'Error al fusionar los expedientes',
				type: 'error',
			});
			console.error(error);
		} finally {
			setLoadingSubmit(false);
		}
	};

	const handleNext = (selectedId?: string, reason?: string) => {
		if (selectedId) setPrimaryEmployeeId(selectedId);
		if (step === 2 && reason) {
			handleSubmit(reason);
			return;
		}
		setStep((prev) => prev + 1);
	};

	const handleBack = () => {
		if (step === 0) {
			onClose();
			return;
		}

		setStep((prev) => prev - 1);
	};

	useEffect(() => {
		if (!open) {
			setStep(0);
			setPrimaryEmployeeId('');
		}
	}, [open]);

	const Component: React.ComponentType<MergedStepComponentProps> =
		useMemo(() => {
			const Element = steps.find((el) => el.id === step)?.component;

			return Element as React.ComponentType<MergedStepComponentProps>;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [step, steps]);

	return (
		<DialogSteps.Root
			open={open}
			title="Fusión de expedientes"
			description="Fusiona expedientes médicos de colaboradores duplicados o con expedientes múltiples."
			steps={steps}
			currentStep={step}
		>
			<Suspense fallback={<div>Cargando...</div>}>
				<Component
					onNext={handleNext}
					onBack={handleBack}
					onClose={() => onClose()}
					employees={employees}
					loading={loadingSubmit}
					primaryEmployeeId={primaryEmployeeId}
				/>
			</Suspense>
		</DialogSteps.Root>
	);
};
